import { useState, useEffect } from "react"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material"
import { useToast } from "@crm-src/hooks/use-toast"
import { useCan } from "@/auth/permissions"
import { useFinancialAccess } from "@crm-src/components/money"

import { ResellerSelector } from "@crm-src/components/reseller-selector"
import { ResellerOnly } from "@crm-src/components/reseller"
import { ProductSelector } from "@crm-src/components/product-selector"
import { MultiUserSelector } from "@crm-src/components/multi-user-selector"
import { UserSelector } from "@crm-src/components/user-selector"
import { MultiSelect } from "@/components/ui/multi-select"
import { AddContactsAfterCreationModal } from "@crm-src/components/add-contacts-after-creation-modal"
import { leadsApi } from "@crm-src/lib/leads-api"
import { dealersApiService } from "@crm-src/lib/dealers-api"
import type { DetailedLead, CreateLeadData } from "@crm-src/types/lead"
import { LEAD_SOURCES, LEAD_PRIORITIES } from "@crm-src/types/lead"
import { PROSPECT_INDUSTRIES, formatIndustry } from "@crm-src/lib/industries"

interface ProspectFormModalProps {
  isOpen: boolean
  onClose: () => void
  prospect?: DetailedLead | null  // If provided, edit mode; if not, create mode
  onSuccess: (prospect: DetailedLead) => void
  initialData?: Partial<CreateLeadData & { assigned_salespeople_ids?: string[] }>
}

export function ProspectFormModal({
  isOpen,
  onClose,
  prospect,
  onSuccess,
  initialData
}: ProspectFormModalProps) {
  const { toast } = useToast()
  // Anyone who may edit a prospect may staff it (CEO, 2026-09-16); crm-api's
  // CustomerManagement enforces the same. This used to be admin-only.
  const canManage = useCan("crm:edit")
  // Financial wall: the Expected Value ($) field is monetary, so it's hidden
  // from a viewer without financial access (crm:financials). The stored value
  // is preserved on save (the field just isn't shown/editable). See money.tsx.
  const canViewFinancials = useFinancialAccess()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [availableDealers, setAvailableDealers] = useState<{label: string, value: string}[]>([])
  const [isDealersLoading, setIsDealersLoading] = useState(false)
  const isEditMode = !!prospect

  // State for showing add contacts modal after creation
  const [showAddContactsModal, setShowAddContactsModal] = useState(false)
  const [createdProspect, setCreatedProspect] = useState<DetailedLead | null>(null)

  // Form data
  const [formData, setFormData] = useState<CreateLeadData & { assigned_salespeople_ids?: string[] }>({
    first_name: "",
    last_name: "",
    email: "",
    company_name: "",
    prospect_source: "",
    industry: "",
    prospected_value: 0,
    prospected_ports: 0,
    reseller_id: null,
    dealer_ids: [],
    assigned_salespeople_ids: [],
    assigned_to_id: undefined,
    notes: "",
    priority: "medium",
    address: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    product_id: undefined,
    product_feature_id: undefined,
  })

  // Initialize form data when modal opens or prospect changes
  useEffect(() => {
    if (isOpen) {
      if (prospect) {
        // Edit mode - populate with existing data
        setFormData({
          first_name: prospect.first_name,
          last_name: prospect.last_name,
          email: prospect.email,
          company_name: prospect.company_name || "",
          prospect_source: prospect.source || "",
          industry: prospect.industry || "",
          prospected_value: prospect.prospected_value || 0,
          prospected_ports: prospect.prospected_ports || 0,
          reseller_id: prospect.reseller?.id ? String(prospect.reseller.id) : null,
          dealer_ids: prospect.dealers?.map(d => String(d.id)) || [],
          assigned_salespeople_ids: prospect.assigned_salespeople?.map(sp => String(sp.user_id)) || [],
          notes: prospect.notes || "",
          priority: prospect.priority,
          address: prospect.address || "",
          city: prospect.city || "",
          state: prospect.state || "",
          country: prospect.country || "",
          postal_code: prospect.postal_code || "",
          product_id: prospect.product?.id ? String(prospect.product.id) : undefined,
          product_feature_id: prospect.product_feature?.id ? String(prospect.product_feature.id) : undefined,
        })
      } else {
        // Create mode - reset to empty or use initialData
        resetForm()
      }
    }
  }, [prospect, isOpen, initialData])

  // Fetch dealers when reseller changes
  useEffect(() => {
    const fetchDealers = async () => {
      if (formData.reseller_id) {
        setIsDealersLoading(true)
        try {
          const dealers = await dealersApiService.getByReseller(formData.reseller_id)
          setAvailableDealers(dealers.map(d => ({ label: d.name, value: String(d.id) })))
        } catch (error) {
          console.error("Failed to fetch dealers", error)
          setAvailableDealers([])
        } finally {
          setIsDealersLoading(false)
        }
      } else {
        setAvailableDealers([])
      }
    }
    fetchDealers()
  }, [formData.reseller_id])

  const resetForm = () => {
    setFormData({
      first_name: initialData?.first_name || "",
      last_name: initialData?.last_name || "",
      email: initialData?.email || "",
      company_name: initialData?.company_name || "",
      prospect_source: initialData?.prospect_source || "",
      industry: initialData?.industry || "",
      prospected_value: initialData?.prospected_value || 0,
      prospected_ports: initialData?.prospected_ports || 0,
      reseller_id: initialData?.reseller_id ? String(initialData.reseller_id) : null,
      dealer_ids: initialData?.dealer_ids || [],
      assigned_salespeople_ids: initialData?.assigned_salespeople_ids || [],
      assigned_to_id: initialData?.assigned_to_id,
      notes: initialData?.notes || "",
      priority: initialData?.priority || "medium",
      address: initialData?.address || "",
      city: initialData?.city || "",
      state: initialData?.state || "",
      country: initialData?.country || "",
      postal_code: initialData?.postal_code || "",
      product_id: initialData?.product_id ? String(initialData.product_id) : undefined,
      product_feature_id: initialData?.product_feature_id ? String(initialData.product_feature_id) : undefined,
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      if (isEditMode && prospect) {
        // Update existing prospect
        await leadsApi.update(prospect.id, formData)
        const updatedResponse = await leadsApi.getById(prospect.id)
        const updatedProspect = updatedResponse.lead

        onSuccess(updatedProspect)
        toast({
          title: "Success",
          description: "Prospect updated successfully",
        })
      } else {
        // Create new prospect
        const response = await leadsApi.create(formData)
        const newProspect = response.lead

        // Don't show toast or close modal yet - show add contacts modal instead
        setCreatedProspect(newProspect)
        setShowAddContactsModal(true)

        // Call onSuccess to update parent list
        onSuccess(newProspect)
      }

      // Only close if not showing add contacts modal (edit mode)
      if (isEditMode) {
        onClose()
      }
    } catch (error: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} prospect:`, error)

      // Handle validation errors
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const errorMessages = error.response.data.errors
        const message = Array.isArray(errorMessages) && errorMessages.length > 0
          ? errorMessages.length === 1 ? errorMessages[0] : errorMessages.join('. ')
          : "Validation error occurred"
        toast({
          title: "Validation Error",
          description: message,
          variant: "destructive",
        })
      } else {
        toast({
          title: "Error",
          description: `Failed to ${isEditMode ? 'update' : 'create'} prospect`,
          variant: "destructive",
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (isSubmitting) return
    onClose()
    if (!isEditMode) {
      resetForm()
    }
  }

  // Handle when add contacts modal closes
  const handleAddContactsClose = () => {
    setShowAddContactsModal(false)
    setCreatedProspect(null)
    onClose() // Close the main prospect form modal
    resetForm()
  }

  return (
    <>
      <Dialog
        open={isOpen && !showAddContactsModal}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { minWidth: 700, maxWidth: "90vw" } }}
      >
        <DialogTitle>{isEditMode ? 'Edit Prospect' : 'Add New Prospect'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {isEditMode
              ? 'Update prospect business information and address details.'
              : 'Create a new prospect to track in your sales pipeline.'
            }
            {canManage && " You can also change who the salespeople are."}
          </DialogContentText>

          <Box component="form" id="prospect-form" onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Company Name */}
              <TextField
                id="company_name"
                label="Company Name"
                size="small"
                fullWidth
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                placeholder="Enter company name"
              />

              {/* Business Fields */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ borderBottom: 1, borderColor: "divider", pb: 1 }}>
                  <Typography component="h4" sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
                    Business Details
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                    Sales and business information
                  </Typography>
                </Box>
                <TextField
                  id="prospect_source"
                  select
                  label="Lead Source"
                  size="small"
                  fullWidth
                  value={formData.prospect_source}
                  onChange={(e) => setFormData({ ...formData, prospect_source: e.target.value })}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="" disabled>
                    Select source
                  </MenuItem>
                  {LEAD_SOURCES.map(source => (
                    <MenuItem key={source} value={source}>{source}</MenuItem>
                  ))}
                </TextField>
                {/* Industry / sector. Set automatically from the sales agent's
                 * vertical on AI-generated prospects (Convert to Prospect),
                 * editable here for every prospect. An out-of-list stored value
                 * (e.g. a legacy raw agent vertical) is preserved as an extra
                 * option so editing other fields never silently wipes it. */}
                <TextField
                  id="industry"
                  select
                  label="Industry / Sector"
                  size="small"
                  fullWidth
                  value={formData.industry || ""}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="">
                    <em>Not set</em>
                  </MenuItem>
                  {formData.industry &&
                    !PROSPECT_INDUSTRIES.includes(formData.industry as (typeof PROSPECT_INDUSTRIES)[number]) && (
                      <MenuItem value={formData.industry}>{formatIndustry(formData.industry)}</MenuItem>
                    )}
                  {PROSPECT_INDUSTRIES.map(industry => (
                    <MenuItem key={industry} value={industry}>{industry}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  id="priority"
                  select
                  label="Priority"
                  size="small"
                  fullWidth
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                >
                  {LEAD_PRIORITIES.map(priority => (
                    <MenuItem key={priority.value} value={priority.value}>
                      {priority.label}
                    </MenuItem>
                  ))}
                </TextField>
                {canViewFinancials && (
                  <TextField
                    id="prospected_value"
                    label="Expected Value ($)"
                    type="number"
                    size="small"
                    fullWidth
                    value={formData.prospected_value || ""}
                    onChange={(e) => setFormData({ ...formData, prospected_value: parseFloat(e.target.value) || 0 })}
                    inputProps={{ min: 0 }}
                  />
                )}
                <TextField
                  id="prospected_ports"
                  label="Expected Ports/Seats"
                  type="number"
                  size="small"
                  fullWidth
                  value={formData.prospected_ports || ""}
                  onChange={(e) => setFormData({ ...formData, prospected_ports: parseInt(e.target.value) || 0 })}
                  inputProps={{ min: 0 }}
                />
                {/* Reseller wall: the reseller picker AND its dependent dealers
                    selector (dealers belong to a reseller) are hidden for a
                    viewer without crm:resellers. See components/reseller.tsx. */}
                <ResellerOnly>
                <Box>
                  <ResellerSelector
                    value={formData.reseller_id}
                    onValueChange={(value) => {
                      // When reseller changes, clear selected dealers as they belong to previous reseller
                      setFormData({ ...formData, reseller_id: value, dealer_ids: [] })
                    }}
                    placeholder="Select a reseller..."
                  />
                </Box>
                <Box>
                  <Typography component="label" sx={{ display: "block", fontSize: 14, fontWeight: 500, mb: 0.5 }}>
                    Dealers
                  </Typography>
                  {isDealersLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        height: 40,
                        px: 1.5,
                        py: 1,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        fontSize: 14,
                        color: "text.secondary",
                        bgcolor: "surfaceSoft",
                      }}
                    >
                      <CircularProgress size={16} color="inherit" />
                      <span>Loading dealers...</span>
                    </Box>
                  ) : formData.reseller_id && availableDealers.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        height: 40,
                        px: 1.5,
                        py: 1,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        fontSize: 14,
                        color: "text.secondary",
                        bgcolor: "surfaceSoft",
                        cursor: "not-allowed",
                      }}
                    >
                      <span>No dealers available for this reseller</span>
                    </Box>
                  ) : (
                    <Box sx={{ ...(formData.reseller_id ? {} : { opacity: 0.5 }) }}>
                      <MultiSelect
                        options={availableDealers}
                        selected={formData.dealer_ids || []}
                        onChange={(selected) => setFormData({ ...formData, dealer_ids: selected })}
                        placeholder={formData.reseller_id ? "Select dealers..." : "Select reseller first"}
                      />
                    </Box>
                  )}
                </Box>
                </ResellerOnly>
                {canManage && (
                  <Box>
                    {isEditMode ? (
                      <MultiUserSelector
                        value={formData.assigned_salespeople_ids || []}
                        onValueChange={(value) => setFormData({ ...formData, assigned_salespeople_ids: value })}
                        label="Assigned Salespeople"
                        placeholder="Select salespeople..."
                      />
                    ) : (
                      <UserSelector
                        value={formData.assigned_to_id}
                        onValueChange={(value) => setFormData({ ...formData, assigned_to_id: value })}
                        label="Assigned Salesperson"
                        placeholder="Select a salesperson..."
                      />
                    )}
                  </Box>
                )}
              </Box>

              {/* Product Selection */}
              <ProductSelector
                productId={formData.product_id}
                productFeatureId={formData.product_feature_id}
                onProductChange={(productId) => setFormData(prev => ({ ...prev, product_id: productId }))}
                onProductFeatureChange={(featureId) => setFormData(prev => ({ ...prev, product_feature_id: featureId }))}
              />

              {/* Address Fields */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  id="address"
                  label="Street Address"
                  size="small"
                  fullWidth
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main Street"
                />

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <TextField
                    id="city"
                    label="City"
                    size="small"
                    fullWidth
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="New York"
                  />
                  <TextField
                    id="state"
                    label="State/Province"
                    size="small"
                    fullWidth
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="NY"
                  />
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <TextField
                    id="country"
                    label="Country"
                    size="small"
                    fullWidth
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="United States"
                  />
                  <TextField
                    id="postal_code"
                    label="Postal Code"
                    size="small"
                    fullWidth
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    placeholder="10001"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="prospect-form"
            variant="contained"
            disabled={isSubmitting}
            {...(isSubmitting ? { startIcon: <CircularProgress size={16} color="inherit" /> } : {})}
          >
            {isSubmitting
              ? (isEditMode ? 'Updating...' : 'Creating...')
              : (isEditMode ? 'Update Prospect' : 'Add Prospect')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Contacts After Creation Modal */}
      {createdProspect && (
        <AddContactsAfterCreationModal
          isOpen={showAddContactsModal}
          onClose={handleAddContactsClose}
          entityId={createdProspect.id}
          entityName={createdProspect.company_name || createdProspect.full_name || "Prospect"}
          entityType="prospect"
        />
      )}
    </>
  )
}
